import React from 'react';
import type { Candidate, MfaPhoneNumberPreferences } from '@axiom/validation';
import { CandidateCommunicationAlertsConst } from '@axiom/const';
import {
  CardHeader,
  Card,
  CardSection,
  List,
  ListItem,
  Well,
  TimezoneDropdown,
  CalendarTimeZoneUtil,
  Checkbox,
  Badge,
  SectionHeader,
  Gutter,
  Paragraph,
  SmallHeader,
  CondensedLabel,
  CondensedMedium,
  Grid,
  GridRow,
  GridColumn,
  Form,
  Radio,
  useApi,
} from '@axiom/ui';

import { CandidateCommunicationAlertsApi } from '../../api/candidate-communication-alerts-api';

import { PreferencesCommunicationsFormSchema } from './preferences-communications-form-schema';

const { NotificationType } = CandidateCommunicationAlertsConst;

export type PreferencesCommunicationsProps = {
  name?: string;
  candidate: Candidate;
  mfa: MfaPhoneNumberPreferences;
};

export const PreferencesCommunications = ({
  name = 'PREFERENCES_COMMUNICATIONS',
  candidate,
  mfa,
}: PreferencesCommunicationsProps) => {
  const [{ data: communicationAlerts }] = useApi(
    CandidateCommunicationAlertsApi.readCommunicationAlerts(candidate.id)
  );

  const { addressCountryCode } = candidate;
  const { notificationsPhoneNumber, notificationsPhoneNumberCountry } = mfa;

  const showOptInTextMessage = addressCountryCode === 'US';
  const isOptInTextMessageDisabled =
    !notificationsPhoneNumber || notificationsPhoneNumberCountry !== 'US';

  const resolvedTimezone = CalendarTimeZoneUtil.getTimeZone(
    communicationAlerts.timezone
  );

  return (
    <Card name={name}>
      <CardHeader>
        <SmallHeader>Communications</SmallHeader>
      </CardHeader>
      <CardSection>
        <Form
          name="PREFERENCES_COMMUNICATIONS_FORM"
          schema={PreferencesCommunicationsFormSchema}
          initialValues={{
            ...communicationAlerts,
            timezone: communicationAlerts.timezone || resolvedTimezone.iana,
            TIMEZONE_DROPDOWN: resolvedTimezone.id,
          }}
          submitOnChange
          onSubmit={async (_changedData, _actions, allValues) => {
            const { TIMEZONE_DROPDOWN: _, ...submitValues } = allValues;
            await CandidateCommunicationAlertsApi.updateCommunicationAlerts(
              candidate.id,
              submitValues
            );
            await CandidateCommunicationAlertsApi.refreshCommunicationAlerts(
              candidate.id
            );
          }}
        >
          {({ setValues, values }) => {
            return (
              <>
                <SectionHeader>Time zone</SectionHeader>
                <Gutter bottom="16px" />
                <Well>
                  <TimezoneDropdown
                    name="TIMEZONE_DROPDOWN"
                    label="Your time zone"
                    onChange={(_value, _e, timezoneObj) => {
                      if (timezoneObj) {
                        void setValues({
                          ...values,
                          timezone: timezoneObj.iana,
                        });
                      }
                    }}
                  />
                </Well>
                <Gutter bottom="16px" />
                <SectionHeader>Communication alerts</SectionHeader>
                <Gutter bottom="8px" />
                <Paragraph name="COMMUNICATION_ALERTS_DESCRIPTION">
                  Choose the notifications you want to receive from Axiom and
                  how you want to receive them. Your selections for Future
                  notifications will apply once they're available.{' '}
                  {showOptInTextMessage
                    ? 'Standard message and data rates may apply.'
                    : ''}
                </Paragraph>
                <Gutter bottom="16px" />
                <List>
                  <ListItem>
                    <Grid>
                      <GridRow>
                        <GridColumn width={8}>
                          <CondensedLabel>Interviews</CondensedLabel>
                          <Gutter bottom="4px" />
                          <CondensedMedium>
                            Email notifications and reminders are always sent
                            for interview requests and scheduling updates.{' '}
                            {showOptInTextMessage
                              ? 'You can also choose to receive them via text message.'
                              : ''}
                          </CondensedMedium>
                        </GridColumn>
                        <GridColumn width={4}>
                          <Checkbox
                            disabled
                            name="communicationAlerts.interviews.optInEmail"
                            displayValue="Email"
                          />
                          {showOptInTextMessage ? (
                            <Gutter top="8px">
                              <Checkbox
                                name="communicationAlerts.interviews.optInTextMessage"
                                disabled={isOptInTextMessageDisabled}
                                displayValue={<>SMS text message</>}
                              />
                            </Gutter>
                          ) : null}
                        </GridColumn>
                      </GridRow>
                    </Grid>
                  </ListItem>
                  <ListItem>
                    <Grid>
                      <GridRow>
                        <GridColumn width={8}>
                          <CondensedLabel>Opportunity Outreach</CondensedLabel>
                          <Gutter bottom="4px" />
                          <CondensedMedium>
                            We will notify you about time-sensitive
                            opportunities.{' '}
                            {showOptInTextMessage
                              ? 'Do you also want these via SMS?'
                              : ''}
                          </CondensedMedium>
                        </GridColumn>
                        <GridColumn width={4}>
                          <Radio
                            name="communicationAlerts.opportunitySuggestions.notificationType"
                            option={NotificationType.EmailOnly}
                            displayValue="Email"
                          />
                          {showOptInTextMessage ? (
                            <>
                              <Gutter top="8px">
                                <Radio
                                  name="communicationAlerts.opportunitySuggestions.notificationType"
                                  option={NotificationType.TextOnly}
                                  displayValue="SMS text message"
                                  disabled={isOptInTextMessageDisabled}
                                />
                              </Gutter>
                              <Gutter top="8px">
                                <Radio
                                  name="communicationAlerts.opportunitySuggestions.notificationType"
                                  option={NotificationType.BothTextAndEmail}
                                  displayValue="Both"
                                  disabled={isOptInTextMessageDisabled}
                                />
                              </Gutter>
                            </>
                          ) : null}
                        </GridColumn>
                      </GridRow>
                    </Grid>
                  </ListItem>
                  <ListItem>
                    <Grid>
                      <GridRow>
                        <GridColumn width={8}>
                          <CondensedLabel>
                            Talent partner communications
                          </CondensedLabel>
                          <Gutter bottom="4px" />
                          <CondensedMedium>
                            Talent partners may reach out through our secure
                            platform for work-related topics like check-ins,
                            potential engagements, or career guidance.
                          </CondensedMedium>
                        </GridColumn>
                        <GridColumn width={4}>
                          <Checkbox
                            name="communicationAlerts.talentPartnerCommunications.optInEmail"
                            displayValue={
                              <>
                                <Badge background="gray">Future</Badge> Email
                              </>
                            }
                          />
                          {showOptInTextMessage ? (
                            <Gutter top="8px">
                              <Checkbox
                                name="communicationAlerts.talentPartnerCommunications.optInTextMessage"
                                disabled={isOptInTextMessageDisabled}
                                displayValue={
                                  <>
                                    <Badge background="gray">Future</Badge> SMS
                                    text message
                                  </>
                                }
                              />
                            </Gutter>
                          ) : null}
                        </GridColumn>
                      </GridRow>
                    </Grid>
                  </ListItem>
                  <ListItem>
                    <Grid>
                      <GridRow>
                        <GridColumn width={8}>
                          <CondensedLabel>Engagement status</CondensedLabel>
                          <Gutter bottom="4px" />
                          <CondensedMedium>
                            Engagement status updates include confirmed
                            extensions or end dates for an active engagement.
                          </CondensedMedium>
                          <Gutter bottom="8px" />
                          <CondensedMedium>
                            Confirmed end dates, utilization changes, reserved
                            status changes.
                          </CondensedMedium>
                        </GridColumn>
                        <GridColumn width={4}>
                          <Checkbox
                            name="communicationAlerts.engagementStatus.optInEmail"
                            displayValue={<>Email</>}
                          />
                          {showOptInTextMessage ? (
                            <Gutter top="8px">
                              <Checkbox
                                name="communicationAlerts.engagementStatus.optInTextMessage"
                                disabled={isOptInTextMessageDisabled}
                                displayValue="SMS text message"
                              />
                            </Gutter>
                          ) : null}
                        </GridColumn>
                      </GridRow>
                    </Grid>
                  </ListItem>
                  <ListItem>
                    <Grid>
                      <GridRow>
                        <GridColumn width={8}>
                          <CondensedLabel>Submission status</CondensedLabel>
                          <Gutter bottom="4px" />
                          <CondensedMedium>
                            Submissions are opportunities a client is currently
                            considering you for. Status changes keep you
                            informed as the submission progresses.
                          </CondensedMedium>
                        </GridColumn>
                        <GridColumn width={4}>
                          <Checkbox
                            name="communicationAlerts.submissionStatus.optInEmail"
                            displayValue={
                              <>
                                <Badge background="gray">Future</Badge> Email
                              </>
                            }
                          />
                          {showOptInTextMessage ? (
                            <Gutter top="8px">
                              <Checkbox
                                name="communicationAlerts.submissionStatus.optInTextMessage"
                                disabled={isOptInTextMessageDisabled}
                                displayValue={
                                  <>
                                    <Badge background="gray">Future</Badge> SMS
                                    text message
                                  </>
                                }
                              />
                            </Gutter>
                          ) : null}
                        </GridColumn>
                      </GridRow>
                    </Grid>
                  </ListItem>
                  <ListItem>
                    <Grid>
                      <GridRow>
                        <GridColumn width={8}>
                          <CondensedLabel>Opportunity feed</CondensedLabel>
                          <Gutter bottom="4px" />
                          <CondensedMedium>
                            Choose how you want to receive instant opportunity
                            feed alerts (if you have these set up in Opportunity
                            Alerts preferences).
                          </CondensedMedium>
                        </GridColumn>
                        <GridColumn width={4}>
                          <Checkbox
                            name="communicationAlerts.opportunityFeed.optInEmail"
                            displayValue={
                              <>
                                <Badge background="gray">Future</Badge> Email
                              </>
                            }
                          />
                          {showOptInTextMessage ? (
                            <Gutter top="8px">
                              <Checkbox
                                name="communicationAlerts.opportunityFeed.optInTextMessage"
                                disabled={isOptInTextMessageDisabled}
                                displayValue={
                                  <>
                                    <Badge background="gray">Future</Badge> SMS
                                    text message
                                  </>
                                }
                              />
                            </Gutter>
                          ) : null}
                        </GridColumn>
                      </GridRow>
                    </Grid>
                  </ListItem>
                  <ListItem>
                    <Grid>
                      <GridRow>
                        <GridColumn width={8}>
                          <CondensedLabel>Axiom announcements</CondensedLabel>
                          <Gutter bottom="4px" />
                          <CondensedMedium>
                            Stay connected with our popular “State of Axiom”
                            meetings, exciting business updates, media coverage,
                            press mentions, professional development
                            opportunities, CLEs, and more.
                          </CondensedMedium>
                        </GridColumn>
                        <GridColumn width={4}>
                          <Checkbox
                            name="communicationAlerts.axiomAnnouncements.optInEmail"
                            displayValue={
                              <>
                                <Badge background="gray">Future</Badge> Email
                              </>
                            }
                          />
                          {showOptInTextMessage ? (
                            <Gutter top="8px">
                              <Checkbox
                                name="communicationAlerts.axiomAnnouncements.optInTextMessage"
                                disabled={isOptInTextMessageDisabled}
                                displayValue={
                                  <>
                                    <Badge background="gray">Future</Badge> SMS
                                    text message
                                  </>
                                }
                              />
                            </Gutter>
                          ) : null}
                        </GridColumn>
                      </GridRow>
                    </Grid>
                  </ListItem>
                  <ListItem>
                    <Grid>
                      <GridRow>
                        <GridColumn width={8}>
                          <CondensedLabel>Lessonly reminders</CondensedLabel>
                          <Gutter bottom="4px" />
                          <CondensedMedium>
                            Receive notifications for new Axiom training
                            assignments and completion status updates.
                          </CondensedMedium>
                        </GridColumn>
                        <GridColumn width={4}>
                          <Checkbox
                            name="communicationAlerts.lessonlyReminderCommunications.optInEmail"
                            displayValue={
                              <>
                                <Badge background="gray">Future</Badge> Email
                              </>
                            }
                          />
                          {showOptInTextMessage ? (
                            <Gutter top="8px">
                              <Checkbox
                                name="communicationAlerts.lessonlyReminderCommunications.optInTextMessage"
                                disabled={isOptInTextMessageDisabled}
                                displayValue={
                                  <>
                                    <Badge background="gray">Future</Badge> SMS
                                    text message
                                  </>
                                }
                              />
                            </Gutter>
                          ) : null}
                        </GridColumn>
                      </GridRow>
                    </Grid>
                  </ListItem>
                </List>
              </>
            );
          }}
        </Form>
      </CardSection>
    </Card>
  );
};
